import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { nestApi } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Send, HelpCircle, Mail } from "lucide-react";
import { toast } from "sonner";

const FAQS = [
  {
    question: "What is Humrahe and what do I do as a Rahe?",
    answer: "Humrahe offers personalized private walking tours led by friendly locals. As a Rahe, you host a flexible, engaging walk, share local insights, and create a warm, memorable experience. Bring your genuine connection to the city, share tips, hidden spots, and personal recommendations, and help guests experience the city beyond the obvious."
  },
  {
    question: "Am I expected to be a professional or licensed tour guide?",
    answer: "No, this is not a requirement. You are a friendly local resident. Prior experience is preferred and sharing historical and cultural insights is appreciated as it adds value to the tour."
  },
  {
    question: "What should I do after receiving a booking?",
    answer: "Reach out to the guest in advance to introduce yourself, confirm preferences, align on the meeting point, and explain how they can recognize you on arrival."
  },
  {
    question: "How are tours assigned?",
    answer: "Tours are assigned on a first-come-first-served basis. If multiple Rahes express interest, the one who responds and confirms availability first is usually assigned to the tour."
  },
  {
    question: "How is my compensation determined?",
    answer: "Compensation varies based on several factors, including the city's cost of living, number of adults, and tour duration. Other factors like demand and specific booking details may also influence the final amount."
  },
  {
    question: "What kind of itinerary should I follow?",
    answer: "There is no fixed itinerary. Tours are flexible and shaped around the guest's interests, pace, and preferences."
  },
  {
    question: "Can I take guests inside paid attractions?",
    answer: "You can suggest paid attractions, but tickets are not included. Guests can purchase them if they wish. If they'd like you to join them, they will cover your ticket. You can also assist them with the ticket purchase if needed."
  },
  {
    question: "What if the guest is late or does not show up?",
    answer: "Message and email the guest when you arrive at the meeting point, and again 5–10 minutes after the start time. Even if they don't reply, you must still reach the meeting point on time. Wait for 20 minutes. If they don't arrive, email the Humrahe Team at bookings@humrahe.com with a photo of you at the meeting point and time-stamped proof of communication with the guest. You are then free to leave, and full compensation applies."
  },
  {
    question: "What if I am running late or cannot attend?",
    answer: "Inform both the guest and the Humrahe Team immediately so arrangements can be made."
  },
  {
    question: "How and when will I be paid?",
    answer: "Payments are processed within 1–2 business days after the tour. You must reply to the post-tour email with the required information. Complete your payment details on the portal to avoid delays. Revolut is preferred, with PayPal or bank transfer as alternatives."
  },
  {
    question: "Should I contact the guest before the tour?",
    answer: "Yes, always. This helps build rapport, confirm expectations, and avoid confusion on the day."
  },
  {
    question: "What should I wear or bring?",
    answer: "Dress comfortably and appropriately for walking. Be presentable, friendly, and ready to guide with confidence."
  },
  {
    question: "Can I take photos or videos during the tour?",
    answer: "Only with the guest's verbal consent. Photos are encouraged. You can share them afterward in reply to the post-tour email if the guest agrees."
  },
  {
    question: "What kind of experience should I aim to provide?",
    answer: "A relaxed, friendly, and personalized walk. Focus on connection, local insights, and making the guest feel welcome. Sharing light historical context about key landmarks and the city enhances the experience."
  },
  {
    question: "What if the guest requests something unusual or outside scope?",
    answer: "Be flexible where reasonable, but don't feel pressured to do anything you're uncomfortable with. Inform the Humrahe Team if unsure."
  },
  {
    question: "Can I bring friends or join other groups?",
    answer: "No. Tours are private, and you should host only the assigned guests."
  },
  {
    question: "Is there a referral program?",
    answer: "You can refer others to become Rahes. Referral opportunities are shared via email when active. If applicable, a bonus is paid after the referred Rahe completes their first successful tour."
  },
  {
    question: "Who do I contact if I need help?",
    answer: "Reach out to the Humrahe Team via email at bookings@humrahe.com for support, updates, or questions."
  }
];

export default function HelpPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data } = await nestApi.get("/rahe-portal/profile");
      setProfile(data);
    } catch {
      // Silently fail
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setSending(true);
    try {
      await nestApi.post("/rahe-portal/help/contact", {
        name: profile?.full_name || user?.firstName || "",
        email: profile?.email || user?.email || "",
        subject: form.subject,
        message: form.message
      });
      toast.success("Message sent successfully! We'll get back to you soon.");
      setForm({ subject: "", message: "" });
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="animate-slideUp space-y-6 max-w-4xl" data-testid="help-page">
      <p className="text-sm text-slate-500">Find answers to common questions or contact our support team</p>

      {/* FAQs */}
      <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-slate-900">Frequently Asked Questions</h3>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map((faq, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-${i}`}>
              <AccordionTrigger className="text-left text-sm font-medium text-slate-700 hover:text-slate-900">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {/* Contact Form */}
      <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-slate-900">Contact Support</h3>
        </div>
        <p className="text-sm text-slate-500 mb-6">
          Can't find what you're looking for? Send us a message and we'll get back to you as soon as possible.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-500">Your Name</Label>
              <Input
                value={profile?.full_name || user?.firstName || ""}
                disabled
                className="mt-1.5 bg-slate-50 text-slate-600"
                data-testid="help-name"
              />
            </div>
            <div>
              <Label className="text-slate-500">Your Email</Label>
              <Input
                value={profile?.email || user?.email || ""}
                disabled
                className="mt-1.5 bg-slate-50 text-slate-600"
                data-testid="help-email"
              />
            </div>
          </div>
          <div>
            <Label>Subject *</Label>
            <Input
              value={form.subject}
              onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))}
              placeholder="What's this about?"
              className="mt-1.5"
              data-testid="help-subject"
            />
          </div>
          <div>
            <Label>Message *</Label>
            <Textarea
              value={form.message}
              onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
              placeholder="Describe your issue or question..."
              className="mt-1.5 min-h-[120px]"
              data-testid="help-message"
            />
          </div>
          <Button
            type="submit"
            disabled={sending}
            className="bg-[#1A1B2E] hover:bg-[#2D2E45]"
            data-testid="help-submit"
          >
            <Send className="w-4 h-4 mr-2" />
            {sending ? "Sending..." : "Send Message"}
          </Button>
        </form>
      </div>
    </div>
  );
}
